import { useRef, useMemo, useEffect, useState, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Edges, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { SRGBColorSpace } from "three";
import { motion, useScroll, useTransform, useMotionValueEvent, cubicBezier } from "framer-motion";
import { useStudio } from "@/context/StudioContext";

const TX = {
  Timber: "/timber_boston_oak.jpg",
  Carpet: "/carpet.jpg",
  Stone: "/stone_taj_mahal.jpg",
  Tile: "/tile_botticino.jpg",
};

// Each piece: sign = which corner it sits in [x,y]; edges = knob per side (0=flat,1=tab,-1=blank).
const QUADRANTS = [
  { key: "Timber", sign: [-1, 1], rough: 0.85, metal: 0.0, edges: { top: 0, left: 0, right: 1, bottom: 1 } },
  { key: "Carpet", sign: [1, 1], rough: 0.95, metal: 0.0, edges: { top: 0, right: 0, left: -1, bottom: -1 } },
  { key: "Tile", sign: [-1, -1], rough: 0.78, metal: 0.0, edges: { bottom: 0, left: 0, top: -1, right: 1 } },
  { key: "Stone", sign: [1, -1], rough: 0.5, metal: 0.05, edges: { bottom: 0, right: 0, left: -1, top: 1 } },
];

const HALF = 0.5;

// Append one jigsaw edge onto a THREE.Shape.
// Exterior edges (k=0) stay perfectly straight to keep the outer square crisp.
// Interior edges (k=±1) have the tab/indent PLUS gentle inward curves on the
// flat sections before and after the tab, giving the internal seams the same
// wavy silhouette as the logo.
function addEdge(shape, start, dir, nrm, L, k) {
  const m = (u, v) => [
    start[0] + dir[0] * (u * L) + nrm[0] * (v * L),
    start[1] + dir[1] * (u * L) + nrm[1] * (v * L),
  ];
  if (!k) {
    const e = m(1, 0);
    shape.lineTo(e[0], e[1]);
    return;
  }
  const head = 0.3, neck = 0.17;
  // Inward curve depth for the flat sections; multiplied by k so the mating
  // piece (k of opposite sign) traces the SAME physical curve and the two
  // pieces still lock together perfectly.
  const INWARD = -0.05 * k;
  // gentle inward arc from edge start (0) to tab base (0.42)
  const p1 = m(0.42, 0);
  const cA = m(0.14, INWARD), cB = m(0.32, INWARD * 0.55);
  shape.bezierCurveTo(cA[0], cA[1], cB[0], cB[1], p1[0], p1[1]);
  const c1 = m(0.5, 0), c2 = m(0.31, k * 0.12), e1 = m(0.37, k * neck);
  shape.bezierCurveTo(c1[0], c1[1], c2[0], c2[1], e1[0], e1[1]);
  const c3 = m(0.33, k * head), c4 = m(0.67, k * head), e2 = m(0.63, k * neck);
  shape.bezierCurveTo(c3[0], c3[1], c4[0], c4[1], e2[0], e2[1]);
  const c5 = m(0.69, k * 0.12), c6 = m(0.5, 0), e3 = m(0.58, 0);
  shape.bezierCurveTo(c5[0], c5[1], c6[0], c6[1], e3[0], e3[1]);
  // gentle inward arc from tab end (0.58) to edge end (1)
  const cC = m(0.68, INWARD * 0.55), cD = m(0.86, INWARD);
  const eEnd = m(1, 0);
  shape.bezierCurveTo(cC[0], cC[1], cD[0], cD[1], eEnd[0], eEnd[1]);
}

function makePuzzleShape(edges) {
  const L = 2 * HALF;
  const shape = new THREE.Shape();
  const BL = [-HALF, -HALF], BR = [HALF, -HALF], TR = [HALF, HALF], TL = [-HALF, HALF];
  shape.moveTo(BL[0], BL[1]);
  addEdge(shape, BL, [1, 0], [0, -1], L, edges.bottom);
  addEdge(shape, BR, [0, 1], [1, 0], L, edges.right);
  addEdge(shape, TR, [-1, 0], [0, 1], L, edges.top);
  addEdge(shape, TL, [0, -1], [-1, 0], L, edges.left);
  return shape;
}

// Map each piece's full bounding box (including outward tabs) into UV 0..1
// so texture flows evenly across the whole piece without stretching or seams.
function makeUVGenerator(edges) {
  const TAB = 0.3; // matches addEdge head depth
  const minX = -HALF - (edges.left === 1 ? TAB : 0);
  const maxX = HALF + (edges.right === 1 ? TAB : 0);
  const minY = -HALF - (edges.bottom === 1 ? TAB : 0);
  const maxY = HALF + (edges.top === 1 ? TAB : 0);
  const w = maxX - minX, h = maxY - minY;
  const uv = (x, y) => new THREE.Vector2((x - minX) / w, (y - minY) / h);
  return {
    generateTopUV(_geo, verts, iA, iB, iC) {
      const ax = verts[iA * 3], ay = verts[iA * 3 + 1];
      const bx = verts[iB * 3], by = verts[iB * 3 + 1];
      const cx = verts[iC * 3], cy = verts[iC * 3 + 1];
      return [uv(ax, ay), uv(bx, by), uv(cx, cy)];
    },
    generateSideWallUV(_geo, verts, iA, iB, iC, iD) {
      const ax = verts[iA * 3], ay = verts[iA * 3 + 1];
      const bx = verts[iB * 3], by = verts[iB * 3 + 1];
      const cx = verts[iC * 3], cy = verts[iC * 3 + 1];
      const dx = verts[iD * 3], dy = verts[iD * 3 + 1];
      return [uv(ax, ay), uv(bx, by), uv(cx, cy), uv(dx, dy)];
    },
  };
}

const easeOutQuint = (x) => 1 - Math.pow(1 - x, 5);
const clamp01 = (x) => Math.min(1, Math.max(0, x));

function Piece({ data, progress, smooth, index, texture, clock, view, isTouch }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const g = new THREE.ExtrudeGeometry(makePuzzleShape(data.edges), {
      depth: 0.3,
      bevelEnabled: true,
      bevelThickness: 0.05,
      bevelSize: 0.04,
      bevelSegments: 3,
      steps: 1,
      curveSegments: 64,
      UVGenerator: makeUVGenerator(data.edges),
    });
    g.translate(0, 0, -0.15);
    return g;
  }, [data.edges]);

  useFrame((state) => {
    const mo = ref.current;
    if (!mo) return;
    // On touch we run a short intro (350ms) so the puzzle assembles once
    // even before any scroll — feels much smoother than snapping to home.
    let sep;
    const t = state.clock.elapsedTime;
    if (clock.start === null) clock.start = t + (isTouch ? 0.05 : 0.5);
    const introDur = isTouch ? 0.55 : 1.5;
    const introDelay = isTouch ? index * 0.04 : index * 0.09;
    const a = clamp01((t - clock.start - introDelay) / introDur);
    const e = easeOutQuint(a);
    // On touch use the damped `smooth` value so pieces glide with a soft
    // catch-up rather than tracking every jittery scroll delta 1:1.
    const scrollV = isTouch && smooth ? smooth.current : progress.current.v;
    sep = Math.max(scrollV, 1 - e);

    const { cx, cy, scale } = view.current;
    const [sx, sy] = data.sign;
    const hx = sx * cx;
    const hy = sy * cy;
    const ox = sx * (cx + 2.6);
    const oy = sy * (cy + 1.9);

    mo.position.set(
      hx + (ox - hx) * sep,
      hy + (oy - hy) * sep,
      sep * (index % 2 === 0 ? 0.9 : -0.45)
    );
    mo.rotation.z = sep * (index % 2 === 0 ? 0.36 : -0.36);
    mo.rotation.x = sep * 0.2;
    mo.rotation.y = sep * (index % 2 === 0 ? 0.15 : -0.15);
    mo.scale.setScalar(scale);
  });

  return (
    <mesh ref={ref} geometry={geo} position={[0, 0, 1]}>
      <meshStandardMaterial
        map={texture}
        color="#ffffff"
        roughness={data.rough}
        metalness={data.metal}
        envMapIntensity={0.85}
      />
      {!isTouch && <Edges threshold={28} color="#9C815F" />}
    </mesh>
  );
}

function Scene({ progress, smooth, view, anisotropy, isTouch }) {
  const group = useRef();
  const clock = useRef({ start: null }).current;
  const { invalidate } = useThree();
  const textures = useTexture(
    [TX.Timber, TX.Carpet, TX.Tile, TX.Stone],
    (loaded) => {
      (Array.isArray(loaded) ? loaded : [loaded]).forEach((t) => {
        t.colorSpace = SRGBColorSpace;
        t.anisotropy = anisotropy;
        t.wrapS = THREE.ClampToEdgeWrapping;
        t.wrapT = THREE.ClampToEdgeWrapping;
        t.offset.set(0, 0);
        t.repeat.set(1, 1);
      });
      // Trigger initial render in demand mode after textures resolve.
      invalidate();
    }
  );
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const cam = state.camera;
    if (Math.abs(cam.position.z - view.current.camZ) > 0.001) {
      cam.position.z = view.current.camZ;
      cam.updateProjectionMatrix();
    }
    if (!group.current) return;
    // On touch: no breathing, no parallax — scene is fully static except for
    // scroll-driven piece positions. Frees main thread for smooth scroll.
    if (isTouch) {
      group.current.rotation.y = 0;
      group.current.rotation.x = 0;
      group.current.position.y = 0;
      return;
    }
    // Desktop: slow bounded drift + damped pointer parallax.
    const targetY = Math.sin(t * 0.12) * 0.14 + state.pointer.x * 0.14;
    const targetX = Math.cos(t * 0.1) * 0.05 - state.pointer.y * 0.12;
    group.current.rotation.y += (targetY - group.current.rotation.y) * 0.035;
    group.current.rotation.x += (targetX - group.current.rotation.x) * 0.035;
    group.current.position.y = Math.sin(t * 0.5) * 0.03;
  });
  return (
    <>
      <ambientLight intensity={isTouch ? 0.7 : 0.5} />
      <directionalLight position={[4, 6, 5]} intensity={1.6} color="#fff6ec" />
      {!isTouch && <directionalLight position={[-3, 2, 4]} intensity={0.6} color="#cdbfae" />}
      {!isTouch && <pointLight position={[-4, -2, 3]} intensity={42} color="#8C7355" distance={14} />}
      {!isTouch && <pointLight position={[0, 0, 4]} intensity={12} color="#f3e7d5" distance={9} />}
      <group ref={group}>
        {QUADRANTS.map((q, i) => (
          <Piece key={q.key} data={q} progress={progress} smooth={smooth} index={i} texture={textures[i]} clock={clock} view={view} isTouch={isTouch} />
        ))}
      </group>
    </>
  );
}

// Damped scroll driver for touch. Each rendered frame runs an ease-out lerp
// from the last smooth value toward the raw scroll target. While the lerp is
// still catching up we call invalidate() again — this creates a self-driven
// requestAnimationFrame chain inside `frameloop="demand"`, so frames only
// keep running as long as motion is settling. Battery-friendly and buttery.
function InvalidateOnScroll({ progress, smooth }) {
  const { invalidate } = useThree();
  const introDoneRef = useRef(false);
  const mountedAt = useRef(performance.now());
  useFrame(() => {
    // 1. Keep rendering during the touch intro window (~1s from mount).
    if (!introDoneRef.current) {
      if (performance.now() - mountedAt.current < 1100) {
        invalidate();
      } else {
        introDoneRef.current = true;
      }
    }
    // 2. Damp the scroll progress toward its target and chain frames while
    //    the lerp is still catching up.
    const target = progress.current.v;
    const cur = smooth.current;
    const next = cur + (target - cur) * 0.14;
    smooth.current = next;
    if (Math.abs(target - next) > 0.003) invalidate();
    else smooth.current = target;
  });
  useEffect(() => {
    const onScroll = () => { invalidate(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    invalidate();
    return () => window.removeEventListener("scroll", onScroll);
  }, [invalidate]);
  return null;
}

export default function Hero() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const progress = useRef({ v: 0 });
  // Damped (eased) scroll value used by touch devices — pieces track this
  // instead of raw scrollY, giving buttery interpolation between finger
  // updates. Desktop still uses `progress` directly (frameloop is `always`).
  const smooth = useRef(0);
  // responsive 3D layout, updated from the measured title box (no re-render needed)
  const view = useRef({ cx: 1.9, cy: 1.35, scale: 0.95, camZ: 6.2 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => { progress.current.v = v; });

  // Adaptive perf caps for mobile / tablet: cap devicePixelRatio, clamp
  // pin length, and tune texture anisotropy so phones stay cool & fluid.
  // Pin lengths tightened on 2026-08-06 to eliminate the "blank period"
  // between hero end and manifesto reveal on touch devices.
  const [perf, setPerf] = useState(() => {
    if (typeof window === "undefined") return { dpr: [1.5, 2], pinVh: 240, aniso: 16, isTouch: false };
    const w = window.innerWidth;
    const touch = window.matchMedia("(pointer: coarse)").matches;
    if (w < 768) return { dpr: [1, 1.2], pinVh: 160, aniso: 2, isTouch: touch };
    if (w < 1024) return { dpr: [1.15, 1.6], pinVh: 190, aniso: 4, isTouch: touch };
    return { dpr: [1.5, 2], pinVh: 240, aniso: 16, isTouch: touch };
  });
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      const touch = window.matchMedia("(pointer: coarse)").matches;
      if (w < 768) setPerf({ dpr: [1, 1.2], pinVh: 160, aniso: 2, isTouch: touch });
      else if (w < 1024) setPerf({ dpr: [1.15, 1.6], pinVh: 190, aniso: 4, isTouch: touch });
      else setPerf({ dpr: [1.5, 2], pinVh: 240, aniso: 16, isTouch: touch });
    };
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onMq = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", onMq);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      mq.removeEventListener("change", onMq);
    };
  }, []);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      let scale, camZ, gapX, gapY;
      if (w < 640) { scale = 0.62; camZ = 8.8; gapX = 0.14; gapY = 0.14; }
      else if (w < 1024) { scale = 0.8; camZ = 7.2; gapX = 0.08; gapY = 0.05; }
      else { scale = 0.95; camZ = 6.2; gapX = 0.08; gapY = 0.05; }
      const fov = 42;
      const halfH = Math.tan((fov / 2) * Math.PI / 180) * camZ;
      const halfW = halfH * (w / h);
      const pxToWorld = (2 * halfH) / h;
      const rect = titleRef.current?.getBoundingClientRect();
      const tHalfW = rect ? (rect.width / 2) * pxToWorld : 1.4;
      const tHalfH = rect ? (rect.height / 2) * pxToWorld : 0.45;
      const pieceHalf = 0.5 * scale;
      const tabReach = 0.32 * scale;
      let cx = tHalfW + gapX + pieceHalf + tabReach;
      let cy = tHalfH + gapY + pieceHalf + tabReach;
      // keep pieces on-screen
      cx = Math.min(cx, halfW - pieceHalf - 0.08);
      cy = Math.min(cy, halfH - pieceHalf - 0.08);
      view.current = { cx, cy, scale, camZ };
    };
    compute();
    const ro = "ResizeObserver" in window ? new ResizeObserver(compute) : null;
    if (ro && titleRef.current) ro.observe(titleRef.current);
    window.addEventListener("resize", compute);
    const t1 = setTimeout(compute, 300);
    const t2 = setTimeout(compute, 900);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(compute);
    return () => {
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
      clearTimeout(t1); clearTimeout(t2);
    };
  }, []);

  // Stage 2 now stays fully visible from 34% to the end of the pin — the
  // hero unpins into the Manifesto with the "Four elements. One foundation."
  // text still on screen, eliminating the trailing void the user reported.
  // Cubic-bezier easing on the crossfade gives it a luxury feel instead of
  // the previous linear ramp.
  const LUXE_EASE = cubicBezier(0.22, 1, 0.36, 1);
  const stage1Opacity = useTransform(scrollYProgress, [0, 0.22], [1, 0], { ease: LUXE_EASE });
  const stage1Y = useTransform(scrollYProgress, [0, 0.22], [0, perf.isTouch ? 0 : -40], { ease: LUXE_EASE });
  const stage2Opacity = useTransform(scrollYProgress, [0.20, 0.34], [0, 1], { ease: LUXE_EASE });

  const line = { hidden: { y: "110%" }, show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }) };

  return (
    <section ref={sectionRef} id="hero" className="relative" style={{ height: reduceMotion ? "100dvh" : `${perf.pinVh}dvh` }}>
      <div
        className="sticky top-0 h-[100dvh] w-full overflow-hidden"
        style={{
          // Promote the pinned hero to its own compositor layer so touch
          // scroll runs on GPU without repainting the puzzle each frame.
          willChange: "transform",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
        }}
      >
        {/* radial ambient glow */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(140,115,85,0.16), rgba(18,19,21,0) 55%)" }} />

        <Canvas
          camera={{ position: [0, 0, view.current.camZ], fov: 42 }}
          dpr={perf.dpr}
          frameloop={reduceMotion || perf.isTouch ? "demand" : "always"}
          gl={{ antialias: true, powerPreference: "high-performance" }}
          className="!absolute inset-0"
        >
          <Suspense fallback={null}>
            <Scene progress={progress} smooth={smooth} view={view} anisotropy={perf.aniso} isTouch={perf.isTouch} />
            {perf.isTouch && <InvalidateOnScroll progress={progress} smooth={smooth} />}
          </Suspense>
        </Canvas>

        {/* Stage 1 copy */}
        <motion.div style={{ opacity: stage1Opacity, y: stage1Y }} className="absolute inset-0 px-6 pointer-events-none">
          {/* Centre phrase — pieces close in tightly around it */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center w-full flex justify-center">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[68vw] max-w-[560px] h-[24vh] pointer-events-none -z-10"
              style={{ background: "radial-gradient(ellipse at center, rgba(18,19,21,0.75) 0%, rgba(18,19,21,0.35) 48%, rgba(18,19,21,0) 74%)" }} />
            <div className="reveal-line">
              <motion.h1 ref={titleRef} custom={0} variants={line} initial="hidden" animate="show"
                className="font-serif-display italic text-[#F5F4F0] font-light leading-none whitespace-nowrap"
                style={{ fontSize: "clamp(1.5rem, 4.6vw, 3.4rem)", letterSpacing: "0.02em", textShadow: "0 4px 40px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.6)" }}>
                The Studio Awaits
              </motion.h1>
            </div>
          </div>

          {/* Tagline — centred, sitting just below the bottom pieces */}
          <div className="absolute left-1/2 bottom-[18%] sm:bottom-[16%] md:bottom-[15%] -translate-x-1/2 w-full flex justify-center text-center">
            <motion.p custom={2} variants={line} initial="hidden" animate="show"
              className="reveal-line text-[#EDE9E2]/90 text-[13px] sm:text-sm md:text-base max-w-[19rem] sm:max-w-md font-body"
              style={{ textShadow: "0 2px 20px rgba(0,0,0,0.75)" }}>
              Simplifying the complex puzzle of element selection into one curated studio.
            </motion.p>
          </div>

          {/* Scroll cue — wrapper owns the centring because framer-motion's
              animated transform on the span would override -translate-x-1/2 */}
          <div className="absolute inset-x-0 bottom-4 flex justify-center">
            <motion.span custom={3} variants={line} initial="hidden" animate="show"
              className="reveal-line -mr-[0.4em] text-[#D6D1CA]/45 text-[9px] sm:text-[10px] tracking-[0.4em] uppercase">
              Scroll to unveil
            </motion.span>
          </div>
        </motion.div>

        {/* Stage 2 material labels */}
        <motion.div style={{ opacity: stage2Opacity }} className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-serif-display italic text-[#F5F4F0]/90 text-3xl md:text-5xl">Four elements. One foundation.</span>
          </div>
          <span className="absolute top-[22%] left-[14%] text-[#C9A876] text-lg md:text-2xl lg:text-3xl tracking-[0.24em] uppercase font-body font-light">Timber</span>
          <span className="absolute top-[22%] right-[14%] text-[#C9A876] text-lg md:text-2xl lg:text-3xl tracking-[0.24em] uppercase font-body font-light">Carpet</span>
          <span className="absolute bottom-[22%] left-[14%] text-[#C9A876] text-lg md:text-2xl lg:text-3xl tracking-[0.24em] uppercase font-body font-light">Tile</span>
          <span className="absolute bottom-[22%] right-[14%] text-[#C9A876] text-lg md:text-2xl lg:text-3xl tracking-[0.24em] uppercase font-body font-light">Stone</span>
        </motion.div>
      </div>
    </section>
  );
}
